import { useEffect, useMemo, useRef } from "react";
import { Routes, Route, Navigate, useParams } from "react-router";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { AuthProvider, useAuth } from "@/context/auth-context";
import { WeekPlanProvider } from "@/context/week-plan-context";
import { FriendsProvider } from "@/context/friends-context";
import { LoginPage } from "@/components/auth/login-page";
import { AppLayout } from "@/layouts/app-layout";
import { WeekLayout } from "@/layouts/week-layout";
import { FriendWeekRoute } from "@/layouts/friend-week-layout";
import type { WeekOwner } from "@/lib/week-owner";
import { weekPath } from "@/lib/week-paths";
import { TodayRedirect } from "@/pages/today-redirect";
import { WeekIndexRedirect } from "@/pages/week-index-redirect";
import { DayPage } from "@/pages/day";
import { ComidasPage } from "@/pages/comidas";
import { ShoppingPage } from "@/pages/shopping";
import { CalendarPage } from "@/pages/calendar";
import { RegimenPage } from "@/pages/regimen";
import { FriendsPage } from "@/pages/friends";
import { PreferencesPage } from "@/pages/preferences";
import "./index.css";

/** Compras is always mine: a friend's Compras link opens my Compras of that week. */
function FriendShoppingRedirect() {
  const { weekId } = useParams<{ weekId: string }>();
  return <Navigate to={weekPath(null, weekId ?? "", "shopping")} replace />;
}

/** The week pages, shared by my weeks and a friend's (friend mode). */
const weekPages = (
  <>
    <Route index element={<WeekIndexRedirect />} />
    <Route path="comidas" element={<ComidasPage />} />
    <Route path=":day" element={<DayPage />} />
  </>
);

function AppRoutes() {
  const { user, plan, loading } = useAuth();
  const queryClient = useQueryClient();

  // Reset all cached query data when a signed-in user signs out or switches
  // accounts, to avoid leaking the previous user's data to the next. Skip the
  // initial null → user transition on page load: this parent effect runs after
  // the pages have mounted and started their queries, and clearing then cancels
  // those in-flight fetches, leaving pages that don't re-render stuck loading.
  const previousUserIdRef = useRef<number | null>(null);
  useEffect(() => {
    const previous = previousUserIdRef.current;
    const current = user?.id ?? null;
    previousUserIdRef.current = current;
    if (previous !== null && previous !== current) queryClient.clear();
  }, [user?.id, queryClient]);

  const selfOwner = useMemo<WeekOwner | null>(
    () => (user && plan ? { kind: "self", userId: user.id, username: user.username, planId: plan.id } : null),
    [user?.id, user?.username, plan?.id],
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user || !selfOwner) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <FriendsProvider>
      <WeekPlanProvider owner={selfOwner}>
        <Routes>
          <Route path="/" element={<TodayRedirect />} />
          <Route element={<AppLayout />}>
            <Route path="/week/:weekId" element={<WeekLayout />}>
              {weekPages}
              <Route path="shopping" element={<ShoppingPage />} />
            </Route>
            <Route path="/friend/:friendId/week/:weekId/shopping" element={<FriendShoppingRedirect />} />
            <Route path="/friend/:friendId/week/:weekId" element={<FriendWeekRoute />}>
              {weekPages}
            </Route>
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/regimen" element={<RegimenPage />} />
            <Route path="/friends" element={<FriendsPage />} />
            <Route path="/preferencias" element={<PreferencesPage />} />
          </Route>
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </WeekPlanProvider>
    </FriendsProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
